import ShuffleIcon from "@mui/icons-material/Shuffle";
import { capitalize, Tooltip } from "@mui/material";
import MuiButton from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import i18next from "i18next";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router";
import BasicInput from "../components/BasicInput";
import Button from "../components/Button";
import ConfirmDialog from "../components/ConfirmDialog";
import ErrorMessage from "../components/ErrorMessage";
import FieldTitle from "../components/FieldTitle";
import Label from "../components/Label";
import Loader from "../components/Loader";
import SimpleDialog from "../components/SimpleDialog";
import * as rolesServices from "../services/rolesServices";
import * as usersServices from "../services/usersServices";
import { routes, utils } from "../utils";
import { User as UserType } from "../utils/types";

function generateRandomString(length = 8) {
	const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
	let result = "";
	for (let i = 0; i < length; i++) {
		const index = Math.floor(Math.random() * chars.length);
		result += chars[index];
	}
	return result;
}

export default function User() {
	const [user, setUser] = React.useState<UserType>({
		id: 0,
		roles: [],
		role: "",
		email: "",
		username: "",
		password: "",
		full_name: "",
	});

	const [errors, setErrors] = React.useState<any>({});
	const [roles, setRoles] = React.useState<any>([]);
	const [editing, setEditing] = React.useState<boolean>(false);
	const [deleteModal, setDeleteModal] = React.useState<boolean>(false);
	const [randomPasswordClicked, setRandomPasswordClicked] = React.useState<boolean>(false);

	const { id } = useParams();
	const navigate = useNavigate();

	const goBack = () => navigate(routes.DASHBOARD);

	const isAdmin = utils.isAdmin();

	const isMyUser = id == utils.getCurrentUser().id;

	useEffect(() => {
		if (id) {
			usersServices.getUser(id).then((user) => {
				setUser(user);
			});
		}
	}, [id]);

	useEffect(() => {
		if ((editing || !id) && utils.isAdmin()) {
			rolesServices.getRoles().then((roles) => setRoles(roles));
		}
	}, [editing, id]);

	const handleOnClickChip = (role: string) => setUser({ ...user, role });

	const validate = () => {
		const errors: any = {};
		if (!user.username) {
			errors.username = i18next.t("user-required");
		}

		if (!user.email || !user.email.includes("@")) {
			errors.email = i18next.t("email-required");
		}

		if (!!!user.role) {
			errors.role = i18next.t("rol-required");
		}
		if (!user.password && !editing) {
			errors.password = i18next.t("password-required");
		}
		if (!user.full_name) {
			errors.full_name = i18next.t("name-surname-required");
		}

		setErrors(errors);

		return Object.keys(errors).length === 0 ? {} : errors;
	};

	const handelCancel = () => {
		if (editing) {
			setEditing(false);
		} else {
			navigate(-1);
		}
	};

	const handleCreateEditUser = async () => {
		try {
			const errors = validate();
			if (Object.keys(errors).length !== 0) {
				return;
			}

			if (user.id) {
				var data: any;
				if (isAdmin) {
					data = user;
				} else {
					data = {
						id: user.id,
						email: user.email,
						password: user.password,
					};
				}
				await usersServices.updateUser(data);
			} else {
				await usersServices.createUser(user);
			}

			toast.success(i18next.t(editing ? "edit-user-success" : "create-user-success"));
			goBack();
		} catch (error: any) {
			toast.error(i18next.t(error?.response?.data?.detail ?? "error"));
		}
	};

	const handleDelete = async () => {
		try {
			await usersServices.deleteUser(user.id);
			toast.success(i18next.t("delete-user-success"));
			navigate(routes.USERS_LIST);
		} catch (error: any) {
			toast.error(i18next.t(error?.response?.data?.detail ?? "error"));
		}
	};

	const showValue = !!id && !editing;

	return (
		<SimpleDialog open={true} onClose={goBack}>
			<ConfirmDialog
				title={i18next.t("delete")}
				handleCancel={() => setDeleteModal(false)}
				handleConfirm={handleDelete}
				subtitle={i18next.t("delete-user-confirm")}
				open={deleteModal}
			/>

			<div className="d-flex justify-content-between">
				<Label
					isTitle
					text={
						id ? (editing ? i18next.t("edit-user") : i18next.t("details-user")) : i18next.t("create-user")
					}
					subText={editing ? i18next.t("required-data") : undefined}
				/>
				{editing && utils.isAdmin() && (
					<div className="text-danger" style={{ cursor: "pointer" }} onClick={() => setDeleteModal(true)}>
						{i18next.t("delete")}
					</div>
				)}
			</div>
			<BasicInput
				label={i18next.t("name-surname")}
				value={user.full_name}
				required={true}
				onChange={(e) => setUser({ ...user, full_name: e.target.value })}
				className="mt-2"
				error={errors.full_name}
				datatestid="fullname-input"
				showValue={showValue || !isAdmin}
			/>
			<BasicInput
				label={i18next.t("user")}
				value={user.username}
				required={true}
				onChange={(e) => setUser({ ...user, username: e.target.value })}
				className="mt-2"
				error={errors.username}
				datatestid="username-input"
				showValue={showValue || !isAdmin}
			/>

			{(!id || (!!id && editing)) && (
				<div className="d-flex">
					<BasicInput
						label={i18next.t("password")}
						value={user.password}
						required={true}
						onChange={(e) => setUser({ ...user, password: e.target.value })}
						type={randomPasswordClicked || !id ? undefined : "password"}
						className="mt-2 flex-grow-1"
						error={errors.password}
						datatestid="password-input"
					/>
					<Tooltip title={i18next.t("generate-password")}>
						<MuiButton
							onClick={() => {
								setRandomPasswordClicked(true);
								setUser({ ...user, password: generateRandomString() });
							}}
							className={"align-self-end ms-2 " + (errors.location ? "mb-4" : "")}
							sx={{ height: "2.4rem", minWidth: "2.4rem" }}
							data-testid={"password-button"}
						>
							<ShuffleIcon className="text-primary" />
						</MuiButton>
					</Tooltip>
				</div>
			)}

			<BasicInput
				label={i18next.t("email")}
				value={user.email}
				required={true}
				onChange={(e) => setUser({ ...user, email: e.target.value })}
				type="email"
				className="mt-2"
				error={errors.email}
				datatestid="email-input"
				showValue={showValue}
			/>

			<FieldTitle label={i18next.t("role")} className={"mt-3"} />

			{showValue || !isAdmin ? (
				<div>{capitalize(i18next.t(user.role))}</div>
			) : (
				<>
					{roles.length > 0 ? (
						<Stack direction="row" spacing={1}>
							{roles.map((rol: string) => (
								<Chip
									key={rol}
									label={capitalize(i18next.t(rol))}
									variant={user.role === rol ? "outlined" : "filled"}
									onClick={() => handleOnClickChip(rol)}
									className={`rounded-3 ${
										user.role === rol ? "border-info text-primary" : "bg-white border"
									}`}
								/>
							))}
						</Stack>
					) : (
						<Loader />
					)}
				</>
			)}
			{errors.role && <ErrorMessage error={errors.role} />}

			<div className="d-flex mt-4 justify-content-end">
				<Button label={i18next.t("cancel")} onClick={handelCancel} className="ms-4" secondary />
				{id ? (
					editing ? (
						<Button
							label={i18next.t("save")}
							datatestid="create-user-button"
							onClick={handleCreateEditUser}
							className="ms-4"
						/>
					) : (
						<Button
							label={i18next.t(isMyUser ? "edit" : "edit-user")}
							datatestid="create-user-button"
							onClick={() => setEditing(true)}
							className="ms-4"
						/>
					)
				) : (
					<Button
						label={i18next.t("create-user")}
						datatestid="create-user-button"
						onClick={handleCreateEditUser}
						className="ms-4"
					/>
				)}
			</div>
		</SimpleDialog>
	);
}
